import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { FeedbackWidgetHandle } from '@fibonaccitec/feedback-widget';

/**
 * Bridges the headless feedback widget to the user-menu chrome. The widget is
 * mounted once by FeedbackWidgetMount (App.tsx, alongside the shell), while the
 * "Reportar problema" / "Meus tickets" entries and the resolve badge live in
 * UserMenu (inside AppShell) — sibling subtrees of the authenticated shell. The
 * mount PUBLISHES the live imperative handle + the polled unseen-resolved count
 * here; UserMenu READS the handle (to open the report or resolved view from the
 * avatar menu) and the count (to paint the resolve badge + the menu indicator).
 *
 * Mirrors FiboChat's FeedbackWidgetProvider (KTD-3). The provider must wrap BOTH
 * UserMenu and the mount — it lives at the App.tsx shell level.
 */
interface FeedbackWidgetValue {
  /** Imperative handle from the mounted widget; null until it mounts (R2). */
  handle: FeedbackWidgetHandle | null;
  /** Resolved-but-unseen ticket count, pushed from the widget's poll (R4). */
  unseenCount: number;
  setHandle: (handle: FeedbackWidgetHandle | null) => void;
  setUnseenCount: (count: number) => void;
}

const FeedbackWidgetCtx = createContext<FeedbackWidgetValue | null>(null);

export function FeedbackWidgetProvider({ children }: { children: ReactNode }) {
  const [handle, setHandleState] = useState<FeedbackWidgetHandle | null>(null);
  const [unseenCount, setUnseenCount] = useState(0);

  // The handle is a CALLABLE (a function with open/close attached). A bare
  // setHandleState(handle) would be read by React as a state-updater and invoked
  // — never stored. Wrap it in an updater that returns the handle so the function
  // value is stored verbatim. Stable identity (the closure captures no state).
  const setHandle = useCallback((next: FeedbackWidgetHandle | null) => {
    setHandleState(() => next);
  }, []);

  // Both setters are stable (setHandle is a useCallback; setUnseenCount is a
  // useState setter), so the memo only re-creates when handle or unseenCount
  // changes.
  const value = useMemo<FeedbackWidgetValue>(
    () => ({ handle, unseenCount, setHandle, setUnseenCount }),
    [handle, unseenCount, setHandle, setUnseenCount],
  );
  return <FeedbackWidgetCtx.Provider value={value}>{children}</FeedbackWidgetCtx.Provider>;
}

/**
 * Nullable so a consumer rendered without the provider (a standalone UserMenu or
 * mount unit test) degrades gracefully rather than throwing. Readers null-check
 * the handle (it is also null before the widget mounts), so the entry points stay
 * inert until the widget is live.
 */
export function useFeedbackWidget(): FeedbackWidgetValue | null {
  return useContext(FeedbackWidgetCtx);
}
